import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import {
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormDropdownTreeComponent,
  FormInputComponent,
  FormInputFileComponent,
  UserGrantsModalComponent,
  UserGroupsModalComponent,
  UserResponsiblitiesModalComponent,
} from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, AdministrativeTitle, FileInputType, PageMode } from "../../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../../shared/directives";
import {
  AttachFile,
  EntityModelType,
  TableButtonClicked,
  UserEntitiesResponsibleFor,
  UserGrantModelType,
  UserGroupModelType,
  UserModelType,
  UserPermissionModelType,
  UserRole,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  EntitiesApiService,
  LanguageService,
  OrgUnitApiService,
  RolesApiService,
  SystemLayoutService,
  UsersApiService,
  ValidatorApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-user-editor",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    DataTableComponent,
    FormActionButtonsComponent,
    FormInputComponent,
    FormDropdownComponent,
    FormDropdownTreeComponent,
    FormDropdownMultiComponent,
    FormInputFileComponent,
    SmartToolTipsDirective,
    FormCheckboxComponent,
  ],
  templateUrl: "./user-editor.html",
  styleUrl: "./user-editor.scss",
})
export class UserEditorComponent implements OnInit {
  private readonly _modalService = inject(NgbModal);
  private readonly _languageService = inject(LanguageService);
  private readonly _router = inject(Router);
  private readonly _toastrService = inject(ToastrService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _smartToolTipsService = inject(SmartToolTipsService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _validatorApiService = inject(ValidatorApiService);
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _orgUnitApiService = inject(OrgUnitApiService);
  private readonly _rolesApiService = inject(RolesApiService);
  private readonly _translateService = inject(TranslateService);

  id = input<number | undefined>(undefined);
  pageMode = input.required<PageMode>();

  isViewMode = computed(() => this.pageMode() === PageMode.view);
  isEditMode = computed(() => this.pageMode() === PageMode.edit);
  entities = computed(() =>
    AdaaHelper.setDropdownArray(this.entitiesCopy(), "id", AdaaHelper.getFieldLanguage("name"))
  );

  private readonly _genericFloatButtons = genericFloatButtons();
  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  private readonly _permissionsCopy = signal<UserPermissionModelType[]>([]);
  private _userCopy: UserModelType | undefined;

  formValidation = signal<ValidatorModelType | undefined>(undefined);
  administrativeTitles = signal<ValueText[]>([]);
  entitiesCopy = signal<EntityModelType[]>([]);
  orgUnits = signal<ValueText[]>([]);
  wtuaePermissions = signal<ValueText[]>([]);
  enablerPermissions = signal<ValueText[]>([]);
  rolesForGrants = signal<UserRole[]>([]);

  readonly ENABLER_KEY = "Enabler Pillars";
  readonly WTUAE_KEY = "We The UAE Pillars";
  readonly AdaaHelper = AdaaHelper;
  readonly Constants = Constants;
  readonly allowedFiles: FileInputType[] = [
    FileInputType.bmp,
    FileInputType.gif,
    FileInputType.jpeg,
    FileInputType.jpg,
    FileInputType.png,
    FileInputType.svg,
  ];

  submitted = false;
  userForm: FormGroup;
  groups: UserGroupModelType[] = [];
  grants: UserGrantModelType[] = [];
  responsiblities: UserEntitiesResponsibleFor[] = [];

  public get profileFile(): AttachFile {
    return {
      id: this.userForm?.get("profilePictureId")?.value,
      filename: this.userForm?.get("profilePicture")?.value
        ? this.userForm?.get("profilePicture")?.value["filename"]
        : "",
    };
  }

  public get profileFileMessage() {
    return `${this._translateService.instant("user.max_file_size")} <br> ${this._translateService.instant("entity.recommended_image_dimensions")}`;
  }

  public ngOnInit() {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._router.navigateByUrl("/console/users");
    });

    this._prepareForm();
    this._initPage();
    this._floatActions();

    if (!this.isViewMode()) this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_USERS);
  }

  public onFilechange(event: AttachFile | null) {
    if (event) {
      this.userForm?.get("profilePictureId")?.setValue(event.id);
      this.userForm?.get("profilePicture")?.setValue({ filename: event.filename });
    } else {
      this.userForm?.get("profilePictureId")?.setValue(null);
      this.userForm?.get("profilePicture")?.setValue({ filename: "" });
    }
  }

  public onEntityChange(id: number) {
    if (id !== this._userCopy?.entityId) this.userForm?.get("orgUnitIds")?.setValue([]);
    this._getOrgUnits(id);
    this._getGrants(id);
  }

  public addUserGroups() {
    const modal = this._modalService.open(UserGroupsModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.idProp = "usersGroupsModal";
    modal.componentInstance.selectedGroups = AdaaHelper.clone(this.groups);

    modal.result.then((e) => {
      if (!AdaaHelper.isDefined(e)) return;
      this.groups = e;
      this.userForm?.get("userGroups")?.setValue(this.groups);
    });
  }

  public removeUserGroup(event: TableButtonClicked) {
    this.groups = this.groups.filter((e) => e.id !== event.data.id);
    this.userForm?.get("userGroups")?.setValue(this.groups);
  }

  public addUserGrant(): void {
    if (!AdaaHelper.isDefined(this.grants)) this.grants = [];

    const modal = this._modalService.open(UserGrantsModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.idProp = "usersGrantsModal";
    modal.componentInstance.entities = AdaaHelper.clone(this.entities());
    modal.componentInstance.userGrants = AdaaHelper.clone(this.grants);
    modal.componentInstance.entitiesCopy = this.entitiesCopy();
    modal.componentInstance.rolesList = this.rolesForGrants();
    modal.componentInstance.userId = this.id();

    modal.result.then((e) => {
      if (!AdaaHelper.isDefined(e)) return;
      this.grants = [...e];
      this.userForm?.get("grants")?.setValue(this.grants);
    });
  }

  public removeUserGrant(event: TableButtonClicked) {
    this.grants = this.grants.filter((e) => (event.data.id ? e.id !== event.data.id : e.fakeId !== event.data.fakeId));
    this.userForm?.get("grants")?.setValue(this.grants);
  }

  public addResponsibility() {
    const modal = this._modalService.open(UserResponsiblitiesModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.idProp = "usersResponsiblitiesModal";
    modal.componentInstance.selectedResponsiblities = AdaaHelper.clone(this.responsiblities);
    modal.componentInstance.entities = this.entities().map((e) => ({
      userId: this.id(),
      entityId: e.value,
      entityNameEN: e.text,
      entityNameAE: e.text,
    }));

    modal.result.then((e) => {
      if (!AdaaHelper.isDefined(e)) return;
      this.responsiblities = e;
    });
  }

  public removeResponsibility(event: TableButtonClicked): void {
    this.responsiblities = this.responsiblities.filter((e) => e.entityId !== event.data.entityId);
  }

  public cancel() {
    this._router.navigateByUrl("/console/users");
  }

  public submit() {
    this.submitted = true;

    if (!this.userForm?.valid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const grants = this.userForm?.get("grants")?.value as UserGrantModelType[];
    if (!grants?.length) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const administrativeTitle = this.userForm?.get("administrativeTitle")?.value;
    if (administrativeTitle) {
      this.userForm?.get("directorGeneral")?.setValue(administrativeTitle === AdministrativeTitle.directorGeneral);
      this.userForm?.get("minister")?.setValue(administrativeTitle === AdministrativeTitle.minister);
    }

    this.userForm?.get("userPermissions")?.setValue(this._getUserPermissions());
    this._saveUser(this.id(), { ...this.userForm?.getRawValue(), entitiesResponsibleFor: this.responsiblities });
  }

  private _initPage() {
    this._loadGlobalData();
    const id = this.id();
    id ? this._initEditMode(id) : this._initCreateMode();
  }

  private _loadGlobalData() {
    forkJoin({
      entities: this._entitiesApiService.getAllV2(),
      permissions: this._usersApiService.getUserPermissions(),
      translation: this._translateService.get(["user.create.minister", "user.create.director_general"]),
    }).subscribe({
      next: (result) => {
        this._handleUserPermissionsDdls(result.permissions.responseData ?? []);
        this.entitiesCopy.set(result.entities.responseData ?? []);
        this.administrativeTitles.set([
          { value: AdministrativeTitle.minister, text: result.translation["user.create.minister"] },
          { value: AdministrativeTitle.directorGeneral, text: result.translation["user.create.director_general"] },
        ]);
      },
    });
  }

  private _initEditMode(id: number) {
    this._usersApiService.getUser(id).subscribe({
      next: (response) => {
        if (response.inError) return;
        const userData = response.responseData;
        if (!userData) return;

        this._userCopy = userData;
        this.groups = userData.userGroups;
        this.grants = userData.grants;
        this.responsiblities = userData.entitiesResponsibleFor ?? [];
        this.userForm?.patchValue(userData);

        this.userForm
          ?.get("administrativeTitle")
          ?.setValue(
            userData.minister
              ? AdministrativeTitle.minister
              : userData.directorGeneral
                ? AdministrativeTitle.directorGeneral
                : null
          );

        const enabler = userData.userPermissions?.filter((e) => e.itemTypeEN === this.ENABLER_KEY);
        const wtu = userData.userPermissions?.filter((e) => e.itemTypeEN === this.WTUAE_KEY);

        this.userForm
          ?.get("enablerIds")
          ?.setValue(enabler?.filter((e) => e.views === AdaaBoolean.Y).map((e) => e.itemId));
        this.userForm?.get("wtuaeIds")?.setValue(wtu?.filter((e) => e.views === AdaaBoolean.Y).map((e) => e.itemId));
      },
    });
  }

  private _initCreateMode() {
    const entityId = AdaaHelper.entity?.id;
    if (!entityId) return;

    if (!AdaaHelper.isPMOEntity()) {
      this.userForm?.get("entityId")?.setValue(entityId);
      this._getOrgUnits(entityId);
    }

    // By default don't get the PMO Roles
    this._getGrants(-1);
  }

  private _getOrgUnits(entityId: number) {
    this._orgUnitApiService.getAllByEntity(entityId).subscribe({
      next: (response) => {
        if (response.inError) return;
        this.orgUnits.set(
          AdaaHelper.setDropdownArray(response.responseData, "id", AdaaHelper.getFieldLanguage("name"), "parentOrgUnit")
        );
      },
    });
  }

  private _getGrants(entityId: number) {
    this._rolesApiService.getAllRoles(AdaaHelper.isPMOEntity(entityId)).subscribe({
      next: (response) => {
        response.responseData = response.responseData.filter((e) => e.enabled === AdaaBoolean.Y);
        this.rolesForGrants.set(response.responseData);
      },
    });
  }

  private _saveUser(userId: number | undefined, userData: UserModelType) {
    this._usersApiService.saveUser(userId ? "update" : "create", userData).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/users");
      },
    });
  }

  private _floatActions() {
    this._genericFloatButtons([{ key: "print", data: undefined }]);
  }

  private _prepareForm() {
    const emailPattern =
      /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    this.userForm = this._formBuilder.group({
      id: { value: null, disabled: this.isViewMode() },
      username: { value: null, disabled: true },
      email: [{ value: null, disabled: this.isViewMode() || this.isEditMode() }, [Validators.pattern(emailPattern)]],
      nameAE: { value: null, disabled: this.isViewMode() },
      nameEN: { value: null, disabled: this.isViewMode() },
      jobTitleAE: { value: null, disabled: this.isViewMode() },
      jobTitleEN: { value: null, disabled: this.isViewMode() },
      entityId: { value: null, disabled: this.isViewMode() || !AdaaHelper.isPMOEntity() },
      orgUnitIds: { value: [], disabled: this.isViewMode() },
      mobilePhone: { value: null, disabled: this.isViewMode() },
      directPhone: { value: null, disabled: this.isViewMode() },
      status: {
        value: this.pageMode() === PageMode.create ? Constants.OBJECT_STATUS.ACTIVE : null,
        disabled: this.isViewMode(),
      },
      enabled: { value: null, disabled: this.isViewMode() },
      directorGeneral: { value: null, disabled: this.isViewMode() },
      minister: { value: null, disabled: this.isViewMode() },
      updateUser: null,
      smClassification: { value: null, disabled: this.isViewMode() },
      nkifpClassification: { value: null, disabled: this.isViewMode() },
      ekpifpClassification: { value: null, disabled: this.isViewMode() },
      sfpClassification: { value: null, disabled: this.isViewMode() },
      opmfpClassification: { value: null, disabled: this.isViewMode() },
      dkpifpClassification: { value: null, disabled: this.isViewMode() },
      grants: null,
      userGroups: null,
      userPermissions: { value: [], disabled: this.isViewMode() },
      profilePicture: null,
      profilePictureId: null,
      adaa4: { value: false, disabled: this.isViewMode() },

      // FE Only
      administrativeTitle: { value: null, disabled: this.isViewMode() },
      enablerIds: { value: [], disabled: this.isViewMode() },
      wtuaeIds: { value: [], disabled: this.isViewMode() },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_USERS, this.userForm)
      .subscribe({
        next: (res) => {
          this.formValidation.set(res.validation);
          this.userForm = res.form;
        },
      });
  }

  private _handleUserPermissionsDdls(permissions: UserPermissionModelType[]) {
    this._permissionsCopy.set(permissions);

    permissions = permissions?.map((e) => ({
      ...e,
      itemNameAE: `${e.itemNameAE} (${e.itemTypeAE})`,
      itemNameEN: `${e.itemNameEN} (${e.itemTypeEN})`,
    }));

    const enabler = permissions.filter((e) => e.itemTypeEN === this.ENABLER_KEY);
    const wtu = permissions.filter((e) => e.itemTypeEN === this.WTUAE_KEY);

    this.enablerPermissions.set(
      AdaaHelper.setDropdownArray(enabler, "itemId", AdaaHelper.getFieldLanguage("itemName"))
    );
    this.wtuaePermissions.set(AdaaHelper.setDropdownArray(wtu, "itemId", AdaaHelper.getFieldLanguage("itemName")));
  }

  private _getUserPermissions() {
    const entityId = this.userForm?.get("entityId")?.value;
    const permissions = this._permissionsCopy();

    if (entityId !== Constants.CONSTANT_PMO_ID) {
      permissions.forEach((e: UserPermissionModelType) => {
        e.views = undefined;
      });
      return permissions;
    }

    const enablerIds = this.userForm?.get("enablerIds")?.value ?? [];
    const wtuaeIds = this.userForm?.get("wtuaeIds")?.value ?? [];

    permissions.forEach((e: UserPermissionModelType) => {
      e.views = enablerIds.includes(e.itemId) || wtuaeIds.includes(e.itemId) ? AdaaBoolean.Y : undefined;
    });

    return permissions;
  }
}
